import type { ReactElement } from "react"
import React from "react"
import _t from "@core/i18n"
import { PeriodInfo } from "@core/period-info"
import { PriceFormat } from "@core/types"
import get from "lodash/get"
import type { LabelVariant } from "@onestore/hel/dist/components/atoms/LabelAtm/LabelAtm.types"
import { PriceVariantType } from "@onestore/hel/dist/components/molecules/PriceMol/priceMol.types"
import BaseProductCardMol from "@onestore/hel/dist/components/molecules/ProductCardMol"
import DetailedPrice from "@gatsby-plugin-generic-page/components/DetailedPrice"
import getProductCardPeriodText from "@gatsby-plugin-generic-page/helpers/getProductCardPeriodText"
import type { ProductCardItem } from "@gatsby-plugin-marketplace/fragments/marketplaceProductCard"
import { usePriceTypeContext } from "~/context/PriceTypeContext"
import usePriceConfigurationQuery from "~/hooks/usePriceConfigurationQuery"
import { selectItemEvent } from "~/lib/analytics"
import assetsSquidexToImageObject from "~/lib/assetsSquidexToImageObject"
import isEmpty from "~/lib/isEmpty"
import Price from "../AdvancedProductCard/components/Price"

type ProductCardMolProps = {
  product: ProductCardItem
  hasSubgrid?: boolean
}

export default function ProductCardMol({
  product,
  hasSubgrid,
}: ProductCardMolProps): ReactElement<ProductCardMolProps> | null {
  const { showDetailedPrices } = usePriceConfigurationQuery()

  function SendSelectItemEvent() {
    selectItemEvent(
      product.flatData,
      periodInfo?.getFormatted(PriceFormat.REGISTER_NETTO_NO_CURRENCY_NO_PERIOD)
    )
  }

  const { currentPriceType } = usePriceTypeContext()

  const saleConfigurationFLatData = get(
    product,
    "flatData.saleConfigurations[0].flatData"
  )

  const hasDefaultPlan =
    saleConfigurationFLatData &&
    !isEmpty(saleConfigurationFLatData.defaultCloudBluePlan)

  const tooltip = hasDefaultPlan
    ? get(
        saleConfigurationFLatData.defaultCloudBluePlan[0],
        "flatData.promotionPriceTooltip[0].flatData.text"
      )
    : undefined

  const defaultCloudBluePlanPeriods = hasDefaultPlan
    ? saleConfigurationFLatData.defaultCloudBluePlan[0].flatData.periods
    : undefined

  const defaultPeriod =
    hasDefaultPlan && !isEmpty(defaultCloudBluePlanPeriods)
      ? defaultCloudBluePlanPeriods.find((period) => period.default)
      : undefined

  const periodInfo = defaultPeriod ? new PeriodInfo(defaultPeriod) : undefined
  const periodText = getProductCardPeriodText(periodInfo, currentPriceType)
  const hasPromotion = periodInfo?.hasPromotion() ?? false

  if (!periodInfo || !product.flatData.pages) {
    return null
  }
  const optionsMapping = get(
    product,
    "flatData.saleConfigurations[0].flatData.optionsMapping"
  )

  const prefix = !isEmpty(optionsMapping) || product.flatData.hasPricePrefix

  const labelData = hasPromotion
    ? {
        children: _t("marketplace.discountTitle"),
        variant: "primaryOutlined" as LabelVariant,
      }
    : saleConfigurationFLatData && saleConfigurationFLatData.label
      ? {
          children: saleConfigurationFLatData.label.text,
          variant: saleConfigurationFLatData.label.variant,
        }
      : undefined

  const price = periodInfo ? (
    <Price
      prefix={prefix ? _t("prices.from") : undefined}
      tooltip={tooltip}
      periodInfo={periodInfo}
      priceType={currentPriceType}
      periodText={periodText}
    />
  ) : null

  const priceInfo = get(
    saleConfigurationFLatData,
    "defaultCloudBluePlan[0].flatData.priceInfo"
  )

  const detailedPriceJSX =
    showDetailedPrices && !isEmpty(periodInfo) ? (
      <DetailedPrice
        variant={{ type: PriceVariantType.SMALL, align: "left" }}
        periodInfo={periodInfo}
        priceInfo={priceInfo}
      />
    ) : null

  const props = {
    backgroundImage: assetsSquidexToImageObject(
      product.flatData.headerCardBg,
      ""
    ),
    logo: {
      image: assetsSquidexToImageObject(product.flatData.headerLogo, ""),
    },
    label: labelData,
    title: product.flatData.name,
    link: {
      to: product.flatData.pages[0]?.flatData.url,
      title: product.flatData.name,
    },
    description: product.flatData.cardDescription,
    price:
      saleConfigurationFLatData.customPriceBox?.priceText ||
      detailedPriceJSX ||
      price,
    onClick: SendSelectItemEvent,
    hasSubgrid,
  }

  return <BaseProductCardMol {...props} />
}
